import { useState } from 'react';

import { css, styled } from '@mui/material';
import { Link, Typography } from '@kc-console/common-ui';
import {
  getNotificationCountLabels,
  getNotificationDisplayMeta,
  hasMultipleNotificationResources,
  NotificationCategory,
  NotificationStatus,
  useScopedLinkBuilder,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { EllipsisText } from '@kc-console/shared-ui';
import {
  IcChevronDownSmallRegular,
  IcChevronUpSmallRegular,
  IcErrorCircleFill,
  IcSuccessCircleFill,
} from '@kep/kcds-icons';

import { getCardStyle } from './constants';

import type { MouseEvent, Ref } from 'react';
import type { Notification, NotificationResource } from '@kc-console/context';
import type { ColorKey } from './constants';

interface NotificationItemCardProps {
  notification: Notification;
  onClose?: () => void;
  onRead?: (notification: Notification) => void;
  onViewNotice?: (notification: Notification) => void;
  /** 무한스크롤 트리거용 — 끝에서 N번째 카드에 연결되는 IntersectionObserver ref. */
  ref?: Ref<HTMLDivElement>;
}

interface MainRowProps {
  expanded: boolean;
}

const resolveResourceLabel = (resource: NotificationResource): string => resource.name;

const NotificationItemCard = ({
  notification,
  onClose,
  onRead,
  onViewNotice,
  ref,
}: NotificationItemCardProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // 콘솔 내 화면 이동 props 팩토리 — 같은 스코프면 router.push(SPA), 다른 스코프면 full reload.
  const { getProps } = useScopedLinkBuilder();

  // 읽음/안읽음에 따른 시각 토큰 일괄 계산 (배경·아이콘·텍스트). 읽음이면 모두 dim 처리.
  const { Icon, surfaceKey, iconColorKey, textColors } = getCardStyle(
    notification.severity,
    Boolean(notification.read),
  );
  const { categoryLabel, time } = getNotificationDisplayMeta(notification);
  const resources = notification.resources ?? [];

  // 리소스가 2개 이상이면 다건 실행 — 이때만 성공/실패 카운트 칩과 상세 보기를 노출한다.
  const hasMultiple = hasMultipleNotificationResources(notification);
  const countLabels = getNotificationCountLabels(notification);
  const isPlatformNotice = notification.category === NotificationCategory.PLATFORM_NOTICE;
  const descriptionLineClamp = isPlatformNotice ? 1 : 2;
  // 단건일 때 노출할 링크 — 리소스 링크가 없으면 알림(그룹) 자체의 link로 폴백한다.
  // 공지는 canViewNotice("공지 상세")가 이동을 담당하므로 폴백 대상에서 제외해 링크 중복 노출을 막는다.
  const singleResourceLink = hasMultiple
    ? null
    : resources[0]?.link || (isPlatformNotice ? null : notification.link) || null;
  const canViewNotice = isPlatformNotice && Boolean(notification.resourceId);

  // 카드 클릭 시 읽음 처리만 한다 — popper는 닫지 않는다(링크 이동 분리 작업 시 재검토 예정).
  const handleClick = () => {
    onRead?.(notification);
  };

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setExpanded(prev => !prev);
  };

  const handleViewNotice = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onViewNotice?.(notification);
  };

  // 리소스 링크 클릭: 카드 클릭 핸들러로의 전파를 막고, 읽음 처리 + popper 닫기를 수행한 뒤
  // getProps가 이동을 담당한다(같은 스코프 → router.push(SPA), 다른 스코프 → full reload).
  // 이전엔 raw <a>(common-ui Link)라 내부 이동도 전체 새로고침이었다.
  const getResourceLinkProps = (link: string) => {
    const linkProps = getProps(link, {
      onClick: () => {
        onRead?.(notification);
        onClose?.();
      },
    });

    return {
      ...linkProps,
      onClick: (event: MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        linkProps.onClick(event);
      },
    };
  };

  return (
    // 읽음/안읽음 구분은 배경 없이 아이콘·카테고리·타이틀·설명 색상의 weaker 처리로만 표현.
    <Root ref={ref}>
      <MainRow expanded={hasMultiple && expanded} onClick={handleClick}>
        <IconWrap surfaceKey={surfaceKey} iconColorKey={iconColorKey}>
          <Icon width={16} height={16} />
        </IconWrap>

        <Body>
          <MetaRow>
            <Typography variant="label/medium/2xs" color={textColors.category}>
              {categoryLabel}
            </Typography>
            <Typography variant="label/regular/2xs" color="text/weakest/default">
              {time}
            </Typography>
          </MetaRow>

          <TitleRow>
            <Title
              variant="label/semibold/lg"
              color={textColors.title}
              direction="horizon"
              placement="top"
            >
              {notification.title}
            </Title>
            {countLabels.map(countLabel => (
              <CountChip key={countLabel.key} variant={countLabel.key}>
                <ChipText
                  variant="label/medium/2xs"
                  color={
                    countLabel.key === 'success'
                      ? 'decorativeText/weak/green'
                      : 'decorativeText/weak/red'
                  }
                >
                  {countLabel.label}
                </ChipText>
              </CountChip>
            ))}
          </TitleRow>

          {notification.description && (
            <Description
              variant="body/normal/sm"
              color={textColors.description}
              direction="vertical"
              lineClamp={descriptionLineClamp}
              placement="top"
              slotProps={{
                wrapper: {
                  width: '100%',
                },
              }}
            >
              {notification.description}
            </Description>
          )}

          {singleResourceLink && (
            <ResourceDetailLink
              {...getResourceLinkProps(singleResourceLink)}
              size="xs"
              external={false}
            >
              {t('layout:notification.item.resource_detail.label')}
            </ResourceDetailLink>
          )}

          {canViewNotice && (
            <ViewNoticeButton type="button" onClick={handleViewNotice}>
              <Typography variant="label/regular/xs" color="text/interactive/default">
                {t('layout:notification.item.notice_detail.label')}
              </Typography>
            </ViewNoticeButton>
          )}

          {hasMultiple && (
            <ToggleButton type="button" onClick={handleToggle} aria-expanded={expanded}>
              <Typography variant="label/medium/xs" color="text/weakest/default">
                {t('layout:task_manager.item.detail.label')}
              </Typography>
              {expanded ? (
                <IcChevronUpSmallRegular width={16} height={16} />
              ) : (
                <IcChevronDownSmallRegular width={16} height={16} />
              )}
            </ToggleButton>
          )}
        </Body>
      </MainRow>

      {hasMultiple && expanded && (
        <GroupBox>
          <GroupList>
            {resources.map(resource => {
              const isError = resource.status === NotificationStatus.ERROR;
              const ItemIcon = isError ? IcErrorCircleFill : IcSuccessCircleFill;
              return (
                <GroupRow key={resource.id}>
                  <GroupIconWrap
                    iconColorKey={isError ? 'icon/danger/default' : 'icon/success/default'}
                  >
                    <ItemIcon width={12} height={12} />
                  </GroupIconWrap>
                  {resource.link ? (
                    <GroupLink {...getResourceLinkProps(resource.link)} size="xs" external={false}>
                      {resolveResourceLabel(resource)}
                    </GroupLink>
                  ) : (
                    <GroupText variant="body/normal/xs" color="text/default">
                      {resolveResourceLabel(resource)}
                    </GroupText>
                  )}
                </GroupRow>
              );
            })}
          </GroupList>
        </GroupBox>
      )}
    </Root>
  );
};

export default NotificationItemCard;

const Root = styled('div')(
  ({ theme }) => css`
    /* 자식(GroupBox)의 margin-bottom이 Root 밖으로 빠져나가지 않도록 새 BFC.
       그래야 GroupBox 아래 12px 영역도 Root 안쪽이 되어 hover 배경이 적용됨. */
    display: flow-root;
    border-top: ${theme.mixins.border('border/weaker/default')};
    background-color: ${theme.color['surface/default']};
    transition: background-color 0.15s ease;

    /* 카드 어디에 hover 하더라도(상세보기 영역 포함) 전체 카드에 hover 강조가 유지된다.
       GroupBox는 자체 배경이 있어 그 영역만 본인 색을 유지. */
    &:hover {
      background-color: ${theme.color['surface/hover']};
    }

    /* 첫 카드 위에는 구분선 없음 — 카드 사이에만 표시.
       탭 하단 라인과의 8px 간격은 카드 '안쪽' padding으로 준다.
       (리스트 컨테이너 padding으로 주면 그 영역이 카드 밖이라 hover 배경이 끊긴다.) */
    &:first-of-type {
      border-top: none;
      padding-top: 8px;
    }

    /* 마지막 카드 아래에도 구분선 추가. */
    &:last-of-type {
      border-bottom: ${theme.mixins.border('border/weaker/default')};
    }
  `,
);

const MainRow = styled('div', {
  shouldForwardProp: prop => prop !== 'expanded',
})<MainRowProps>(
  ({ expanded }) => css`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 12px ${expanded ? '6px' : '12px'};
    cursor: pointer;
  `,
);

const IconWrap = styled('div', {
  shouldForwardProp: prop => prop !== 'surfaceKey' && prop !== 'iconColorKey',
})<{
  iconColorKey: ColorKey;
  surfaceKey: ColorKey;
}>(
  ({ theme, surfaceKey, iconColorKey }) => css`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 100px;
    background-color: ${theme.color[surfaceKey]};
    color: ${theme.color[iconColorKey]};
  `,
);

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const MetaRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TitleRow = styled('div')`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
`;

const Title = styled(EllipsisText)`
  flex: 0 1 auto;
  min-width: 0;
`;

const CountChip = styled('div', { shouldForwardProp: prop => prop !== 'variant' })<{
  variant: 'success' | 'error';
}>(
  ({ theme, variant }) => css`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    height: 20px;
    min-width: 20px;
    padding: 0 6px 0 4px;
    border-radius: 99px;
    border: 1px solid
      ${variant === 'success'
        ? theme.color['decorativeBorder/green']
        : theme.color['decorativeBorder/red']};
    background-color: ${variant === 'success'
      ? theme.color['decorativeSurface/weakest/green']
      : theme.color['decorativeSurface/weakest/red']};
    white-space: nowrap;
  `,
);

// 칩 내부 텍스트 — line-height를 1로 줄여 글자 박스를 글리프에 맞춰 수직 중앙정렬을 정확히 맞춘다.
const ChipText = styled(Typography)`
  line-height: 1;
`;

const Description = styled(EllipsisText)`
  /* Title과의 세로 간격만 4px. */
  margin-top: 4px;
`;

const ResourceDetailLink = styled(Link)`
  margin-top: 6px;
`;

const ViewNoticeButton = styled('button')(
  () => css`
    align-self: flex-start;
    margin-top: 6px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
  `,
);

const ToggleButton = styled('button')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    align-self: flex-start;
    padding: 4px 0;
    margin-top: 2px;
    background: none;
    border: none;
    cursor: pointer;
    /* chevron 색을 "상세 보기" 텍스트(text/weakest/default)와 맞춤. */
    color: ${theme.color['text/weakest/default']};

    &:focus-visible {
      outline: 2px solid ${theme.color['border/select']};
      outline-offset: 2px;
      border-radius: 2px;
    }
  `,
);

const GroupBox = styled('div')(
  ({ theme }) => css`
    /* 좌측: padding12 + icon36 + gap12 = 60px (본문 텍스트와 정렬), 우측 12px. */
    margin: 0 12px 12px 60px;
    padding: 8px 12px;
    border: ${theme.mixins.border('border/weaker/default')};
    background-color: ${theme.color['surface/neutral/weakest/default']};
    max-height: 96px;
    overflow-y: auto;
  `,
);

const GroupList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GroupRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const GroupIconWrap = styled('div', { shouldForwardProp: prop => prop !== 'iconColorKey' })<{
  iconColorKey: ColorKey;
}>(
  ({ theme, iconColorKey }) => css`
    flex-shrink: 0;
    line-height: 0;
    margin-top: 3px;
    color: ${theme.color[iconColorKey]};
  `,
);

const GroupText = styled(Typography)`
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const GroupLink = styled(Link)`
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
`;
